// =============================================================================
//  RÈGLEMENT ET ENVOI D'UNE FACTURE — blocs communs à l'écran standard
//  (FacturesDomaine) et à l'écran simplifié (FactureSimple, profil rénovation).
// =============================================================================
//  Le serveur décide, l'écran obéit : `paiement_possible`, `reste_a_payer`,
//  `paiements`, `envoi_possible`, `envoi`, `statut_libelle`. Aucun cycle ni
//  montant n'est recalculé ici. Un seul endroit pour les deux écrans.
// =============================================================================
import { useEffect, useState } from "react";
import ChampDate from "../ui/champ-date";
import { Send, Wallet, Loader2 } from "lucide-react";

const etiquette = { fontSize: 12, color: "var(--oh-text-secondary)", fontWeight: 500 };
const carte = {
  background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
  borderRadius: 12, padding: 16,
};
const champ = {
  height: 36, borderRadius: 8, padding: "0 12px", fontSize: 13,
  background: "var(--oh-bg-surface)", color: "var(--oh-text)",
  border: "1px solid var(--oh-border)", width: "100%",
};
const btnSecondaire = {
  display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 12px",
  borderRadius: 8, fontSize: 13, fontWeight: 500, minHeight: 36,
  background: "transparent", border: "1px solid var(--oh-accent)",
  color: "var(--oh-accent-text)",
};
const nombre = { fontVariantNumeric: "tabular-nums" };

export const MODES_PAIEMENT = [
  { valeur: "virement", libelle: "Virement" },
  { valeur: "cheque", libelle: "Chèque" },
  { valeur: "especes", libelle: "Espèces" },
  { valeur: "autre", libelle: "Autre" },
];
const LIBELLE_MODE = Object.fromEntries(MODES_PAIEMENT.map((m) => [m.valeur, m.libelle]));

export function euros(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export function dateFR(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR");
}

function aujourdhuiISO() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Règlements enregistrés + formulaire « Enregistrer un paiement ». */
export function BlocPaiement({ facture, occupe, onAgir }) {
  const f = facture;
  const reste = Number(f.reste_a_payer);
  const [montant, setMontant] = useState("");
  const [date, setDate] = useState(aujourdhuiISO());
  const [mode, setMode] = useState("virement");
  const [reference, setReference] = useState("");
  // Le montant proposé suit le reste à payer renvoyé par le serveur.
  useEffect(() => {
    setMontant(Number.isFinite(reste) && reste > 0 ? String(reste) : "");
  }, [f.id, reste]);

  const paiements = f.paiements || [];
  const montantOk = montant !== "" && Number(montant) > 0;
  if (!f.paiement_possible && paiements.length === 0) return null;

  const enregistrer = async () => {
    const corps = { montant: Number(montant), date, mode };
    if (reference.trim()) corps.reference = reference.trim();
    const fait = await onAgir(`/${f.id}/paiement`, corps);
    if (fait) setReference("");
  };

  return (
    <div style={{ ...carte, display: "grid", gap: 12 }} data-testid="facture-paiements">
      <p style={{ ...etiquette, margin: 0 }}>Paiements</p>
      {paiements.length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 4 }}
            data-testid="facture-paiements-liste">
          {paiements.map((p, i) => (
            <li key={p.id || i} style={{ fontSize: 13, color: "var(--oh-text)", ...nombre }}
                data-testid={`facture-paiement-${i}`}>
              {dateFR(p.date)} · {euros(p.montant)} · {LIBELLE_MODE[p.mode] || "Autre"}
              {p.reference ? ` · ${p.reference}` : ""}
            </li>
          ))}
        </ul>
      )}
      {Number.isFinite(reste) && (
        <p style={{ margin: 0, fontSize: 13, color: "var(--oh-text)", ...nombre }}
           data-testid="facture-reste-a-payer">
          {reste > 0 ? <>Reste à payer : <strong style={{ fontWeight: 500 }}>{euros(reste)}</strong></>
                     : "Facture entièrement réglée."}
        </p>
      )}
      {f.paiement_possible && (
        <div style={{ display: "grid", gap: 12,
                      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                      alignItems: "end" }}>
          <div>
            <label style={etiquette} htmlFor="paiement-montant">Montant (€)</label>
            <input id="paiement-montant" type="number" step="0.01" min="0" style={champ}
                   data-testid="paiement-montant" value={montant} disabled={occupe}
                   onChange={(e) => setMontant(e.target.value)} />
          </div>
          <div>
            <label style={etiquette} htmlFor="paiement-date">Date</label>
            <ChampDate id="paiement-date" style={champ} data-testid="paiement-date"
                   value={date} max={aujourdhuiISO()} disabled={occupe}
                   onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label style={etiquette} htmlFor="paiement-mode">Mode</label>
            <select id="paiement-mode" style={champ} data-testid="paiement-mode"
                    value={mode} disabled={occupe} onChange={(e) => setMode(e.target.value)}>
              {MODES_PAIEMENT.map((m) => (
                <option key={m.valeur} value={m.valeur}>{m.libelle}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={etiquette} htmlFor="paiement-reference">Référence (facultatif)</label>
            <input id="paiement-reference" style={champ} data-testid="paiement-reference"
                   value={reference} disabled={occupe} maxLength={120}
                   onChange={(e) => setReference(e.target.value)} />
          </div>
          <button type="button" style={{ ...btnSecondaire, cursor: occupe ? "wait" : "pointer" }}
                  data-testid="paiement-enregistrer"
                  disabled={occupe || !montantOk || !date} onClick={enregistrer}>
            <Wallet style={{ width: 14, height: 14 }} /> Enregistrer un paiement
          </button>
        </div>
      )}
    </div>
  );
}

function phraseEnvoi(envoi) {
  if (!envoi) return null;
  const quand = dateFR(envoi.sent_at);
  if (envoi.status === "sent") return `Envoyée à ${envoi.recipient} le ${quand}.`;
  if (envoi.status === "skipped_preprod") {
    return `Envoi simulé le ${quand} (environnement de test) : aucun e-mail n'est parti vers ${envoi.recipient}.`;
  }
  return `L'envoi à ${envoi.recipient} a échoué le ${quand}.`;
}

/** Bouton « Envoyer au client » + trace du dernier envoi. */
export function BoutonEnvoi({ facture, occupe, onAgir }) {
  const f = facture;
  if (f.envoi_possible === false) return null;
  return (
    <button type="button" style={{ ...btnSecondaire, cursor: occupe ? "wait" : "pointer",
                                   borderColor: "var(--oh-border)",
                                   color: "var(--oh-text-secondary)" }}
            data-testid="facture-envoyer" disabled={occupe}
            onClick={() => onAgir(`/${f.id}/envoyer`, {})}>
      {occupe ? <Loader2 style={{ width: 14, height: 14 }} />
              : <Send style={{ width: 14, height: 14 }} />}
      Envoyer au client
    </button>
  );
}

export function TraceEnvoi({ facture }) {
  const phrase = phraseEnvoi(facture.envoi);
  if (!phrase) return null;
  const echec = facture.envoi.status === "failed";
  return (
    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6,
                color: echec ? "var(--oh-stat-red-text)" : "var(--oh-text-secondary)" }}
       data-testid="facture-envoi-trace">
      {phrase}
    </p>
  );
}
